import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { useEffect } from 'react';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import { notifyCreditsChanged } from '../../services/creditsApi';

// Icons
import AssignmentIcon from '@mui/icons-material/Assignment';
import ApiIcon from '@mui/icons-material/Api';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HttpIcon from '@mui/icons-material/Http';
import DescriptionIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// --- Shared look & feel (matches External/Dark Web/Vishing) ---
const HeaderCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `linear-gradient(135deg, ${PRIMARYCOLOR} 0%, ${darken(PRIMARYCOLOR, 0.5)} 100%)`,
  color: '#fff',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
}));

const SectionCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  borderRadius: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
}));

const SubCard = styled(Box)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(2.5),
  border: '1px solid rgba(0,0,0,0.08)',
  background: 'rgba(0,0,0,0.02)'
}));

const StepIconContainer = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active
    ? theme.palette.primary.main
    : ownerState.completed
    ? theme.palette.success.main
    : theme.palette.grey[300],
  color: '#fff',
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: ownerState.active ? '0 0 0 6px rgba(25,118,210,0.15)' : 'none'
}));

function CustomStepIcon(props) {
  const { active, completed, icon } = props;
  const mapping = {
    1: <AssignmentIcon fontSize="small" />, // Project Details
    2: <ApiIcon fontSize="small" />,        // Configure API (URL + Docs + Auth)
    3: <CloudUploadIcon fontSize="small" />,// Deploy
    4: <EmojiEventsIcon fontSize="small" /> // Done
  };
  return (
    <StepIconContainer ownerState={{ active, completed }}>
      {mapping[icon] || <AssignmentIcon fontSize="small" />}
    </StepIconContainer>
  );
}

const steps = ['Project Details', 'Configure API', 'Deploy Agent', 'Done'];

const CreateApiPentest = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [projectName, setProjectName] = useState('');
  const [organizationId, setOrganizationId] = useState(-1);
  const [organizations, setOrganizations] = useState([{ id: -1, name: 'None' }]);
  const [noAuth, setNoAuth] = useState(false);


  // Config step
  const [apiUrl, setApiUrl] = useState('');
  const [urlError, setUrlError] = useState(false);
  const [swaggerText, setSwaggerText] = useState('');
  const [authHeaders, setAuthHeaders] = useState([]); // [{key, value}]
  const [authType, setAuthType] = useState('');
  const [bearerToken, setBearerToken] = useState('');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [customKey, setCustomKey] = useState('');
  const [customValue, setCustomValue] = useState('');

  // Deploy step
  const [isDeploying, setIsDeploying] = useState(false);


  const prependNone = (list = []) => {
  const cleaned = (list || []).filter(o => o && Number(o.id) !== -1);
  return [{ id: -1, name: 'None' }, ...cleaned];
};

function loadOrganizationsBasic() {
  const token = sessionStorage.getItem('token');
  if (!token) {
    try { navigate('/'); } catch { /* optional if navigate not available */ }
    return;
  }
  fetch(`${BASEURL}/organizations?basic=1`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
    .then(r => r.json())
    .then(data => {
      if (data?.error) {
        console.error(data.message || 'Error fetching organizations (basic)');
        setOrganizations([{ id: -1, name: 'None' }]);
        setOrganizationId(-1);
        return;
      }
      const list = Array.isArray(data?.organizations) ? data.organizations : [];
      setOrganizations(prependNone(list));
      setOrganizationId(-1); // default selection
    })
    .catch(err => {
      console.error('Failed to fetch organizations (basic):', err);
      setOrganizations([{ id: -1, name: 'None' }]);
      setOrganizationId(-1);
    });
}

// call once on mount
useEffect(() => {
  loadOrganizationsBasic();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // --- Dropzone for OpenAPI/Swagger ---
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') setSwaggerText(result);
    };
    reader.readAsText(file);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/json': ['.json'], 'application/x-yaml': ['.yaml', '.yml'] },
    multiple: false
  });

  // --- Helpers ---
  const validateUrl = (url) => {
    const valid = /^https?:\/\/.+/i.test(url);
    setUrlError(!valid);
    return valid;
  };

  const handleAddAuthHeader = () => {
    let key = '', value = '';
    if (authType === 'Bearer') {
      key = 'Authorization';
      value = `Bearer ${bearerToken.trim()}`;
    } else if (authType === 'Basic') {
      key = 'Authorization';
      const encoded = btoa(`${basicUsername}:${basicPassword}`);
      value = `Basic ${encoded}`;
    } else if (authType === 'Custom') {
      key = customKey.trim();
      value = customValue.trim();
    }
    if (!key || !value) return;

    setAuthHeaders((prev) => [...prev, { key, value }]);
    setAuthType('');
    setBearerToken('');
    setBasicUsername('');
    setBasicPassword('');
    setCustomKey('');
    setCustomValue('');
  };

  const removeAuthHeader = (idx) => {
    setAuthHeaders((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveAndDeploy = () => {
    if (!projectName.trim() || !validateUrl(apiUrl) || !swaggerText) return;

    setIsDeploying(true);
    const token = sessionStorage.getItem('token');

    fetch(`${BASEURL}/pentests/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        name: projectName.trim(),
        scope: {
          api_url: apiUrl.trim(),
          swagger: swaggerText,
          auth_headers: authHeaders.reduce((acc, { key, value }) => {
            acc.push({ [key]: value });
            return acc;
          }, [])
        },
        organizationId: organizationId
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to create API pentest');
        return res.json();
      })
      .then((data) => {
        notifyCreditsChanged({ reason: 'pentest-created', pentestId: data?.pentestId });
        setIsDeploying(false);
        setActiveStep(3); // Done
      })
      .catch((err) => {
        console.error(err);
        setIsDeploying(false);
      });
  };

  function ConfigStepIcon(props) {
  const { active, completed, icon } = props;
  const mapping = {
    1: <HttpIcon fontSize="small" />,
    2: <DescriptionIcon fontSize="small" />,
    3: <LockIcon fontSize="small" />
  };
  return (
    <StepIconContainer ownerState={{ active, completed }}>
      {mapping[icon] || <HttpIcon fontSize="small" />}
    </StepIconContainer>
  );
}
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <HeaderCard>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Create API Pentest Project
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              Test REST/GraphQL endpoints, auth flows, and business logic powered by your OpenAPI/Swagger.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              icon={<ApiIcon />}
              label="API Testing"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" }, mr: 1 }}
            />
            <Chip
              icon={<DescriptionIcon />}
              label="OpenAPI / Swagger"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, i) => (
                <Step key={i}>
                  <StepLabel StepIconComponent={CustomStepIcon}>
                    <Typography sx={{ color: '#fff' }}>{label}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
      </HeaderCard>

      {/* Step 0: Project Details */}
      {activeStep === 0 && (
        <SectionCard sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Project Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Give your project a clear, searchable name.
              </Typography>

              <TextField
                fullWidth
                label="Project Name"
                placeholder="e.g., Acme – API Pentest – Q3"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth>
                <InputLabel id="org-select-label">Organization</InputLabel>
                <Select
                  labelId="org-select-label"
                  label="Organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(Number(e.target.value))}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!projectName.trim()}>
                  Next
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                sx={{
                  width: '100%',
                  minHeight: 220,
                  borderRadius: 3,
                  p: 3,
                  bgcolor: 'rgba(210, 25, 25, 0.06)',
                  border: '1px dashed rgba(210, 25, 25, 0.25)'
                }}
              >
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  WARNING
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This agent is still in BETA and is actively being worked on. If using review findings for false positives before taking action. You may also encounter bugs or unexpected behavior. Please report any issues to help us improve the service.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* Step 1: Configure API (URL + Docs + Auth) */}
      {activeStep === 1 && (
  <SectionCard sx={{ p: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
      Configure API
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Complete these three items so we can test your API: Base URL, OpenAPI/Swagger, and Authentication.
    </Typography>

    {/* Completion checklist */}
    {(() => {
      const baseUrlComplete = /^https?:\/\/.+/i.test(apiUrl);
      const docsComplete = !!swaggerText.trim();
      const authComplete = noAuth || authHeaders.length > 0;
      const cfgActiveStep = !baseUrlComplete ? 0 : !docsComplete ? 1 : 2;

      return (
        <>
          <Stepper alternativeLabel activeStep={cfgActiveStep} sx={{ mb: 2 }}>
            <Step completed={baseUrlComplete}>
              <StepLabel StepIconComponent={ConfigStepIcon}>Base URL</StepLabel>
            </Step>
            <Step completed={docsComplete}>
              <StepLabel StepIconComponent={ConfigStepIcon}>OpenAPI</StepLabel>
            </Step>
            <Step completed={authComplete}>
              <StepLabel StepIconComponent={ConfigStepIcon}>Auth</StepLabel>
            </Step>
          </Stepper>
          <Divider sx={{ mb: 3 }} />
        </>
      );
    })()}

    <Grid container spacing={3}>
      {/* API URL */}
      <Grid item xs={12} md={6}>
        <SubCard>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label="Step 1 of 3" sx={{ mr: 1 }} />
              <HttpIcon fontSize="small" /> API Base URL
            </Typography>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="API URL (e.g., https://api.example.com)"
            value={apiUrl}
            error={urlError}
            helperText={urlError ? 'Must start with http:// or https://' : ' '}
            onChange={(e) => {
              setApiUrl(e.target.value);
              if (urlError) validateUrl(e.target.value);
            }}
            onBlur={(e) => validateUrl(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HttpIcon />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 44, alignItems: 'center' },
              '& .MuiInputBase-input': { py: 0 }
            }}
          />
        </SubCard>
      </Grid>

      {/* OpenAPI / Swagger */}
      <Grid item xs={12} md={6}>
        <SubCard>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label="Step 2 of 3" sx={{ mr: 1 }} />
              <DescriptionIcon fontSize="small" /> OpenAPI / Swagger
            </Typography>
          </Box>

          <Paper
            variant="outlined"
            {...getRootProps()}
            sx={{
              borderStyle: 'dashed',
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
              mb: 2
            }}
          >
            <input {...getInputProps()} />
            <Typography>
              {isDragActive ? 'Drop the file here…' : "Drag & drop file here, or click to select (.json, .yaml)"}
            </Typography>
          </Paper>
          <TextField
            fullWidth
            placeholder="…or paste your OpenAPI/Swagger here"
            multiline
            minRows={6}
            value={swaggerText}
            onChange={(e) => setSwaggerText(e.target.value)}
          />
        </SubCard>
      </Grid>

      {/* Auth Headers */}
      <Grid item xs={12}>
        <SubCard>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label="Step 3 of 3" sx={{ mr: 1 }} />
              <LockIcon fontSize="small" /> Authentication
            </Typography>
            <FormControlLabel
              control={<Switch checked={noAuth} onChange={(e) => setNoAuth(e.target.checked)} />}
              label="No authentication required"
            />
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ opacity: noAuth ? 0.5 : 1 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" disabled={noAuth}>
                <InputLabel>Header Type</InputLabel>
                <Select
                  label="Header Type"
                  value={authType}
                  onChange={(e) => setAuthType(e.target.value)}
                >
                  <MenuItem value="Bearer">Authorization: Bearer</MenuItem>
                  <MenuItem value="Basic">Basic HTTP</MenuItem>
                  <MenuItem value="Custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {authType === 'Bearer' && !noAuth && (
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  size="small"
                  label="Token"
                  value={bearerToken}
                  onChange={(e) => setBearerToken(e.target.value)}
                />
              </Grid>
            )}

            {authType === 'Basic' && !noAuth && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Username"
                    value={basicUsername}
                    onChange={(e) => setBasicUsername(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Password"
                    value={basicPassword}
                    onChange={(e) => setBasicPassword(e.target.value)}
                  />
                </Grid>
              </>
            )}

            {authType === 'Custom' && !noAuth && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Header Key"
                    value={customKey}
                    onChange={(e) => setCustomKey(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Header Value"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md="auto">
              <Button
                variant="contained"
                onClick={handleAddAuthHeader}
                disabled={
                  noAuth ||
                  !authType ||
                  (authType === 'Bearer' && !bearerToken.trim()) ||
                  (authType === 'Basic' && (!basicUsername || !basicPassword)) ||
                  (authType === 'Custom' && (!customKey.trim() || !customValue.trim()))
                }
                sx={{ height: 44 }}
              >
                Add Header
              </Button>
            </Grid>
          </Grid>

          {/* Headers table */}
          <Paper
            elevation={0}
            sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
              <Typography fontWeight={800}>Headers ({authHeaders.length})</Typography>
              {authHeaders.length > 0 && (
                <Tooltip title="Clear all">
                  <span>
                    <Button color="inherit" size="small" onClick={() => setAuthHeaders([])}>
                      Clear All
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={240}>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell width={80} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {authHeaders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        {noAuth ? 'Authentication disabled for this project.' : 'No headers added yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  authHeaders.map(({ key, value }, i) => (
                    <TableRow key={`${key}-${i}`} hover>
                      <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {key}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {value}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => removeAuthHeader(i)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </SubCard>
      </Grid>
    </Grid>

    {/* Footer controls */}
    {(() => {
      const okUrl = /^https?:\/\/.+/i.test(apiUrl);
      const okDocs = !!swaggerText.trim();
      const okAuth = noAuth || authHeaders.length > 0;

      return (
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={() => setActiveStep(0)}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => setActiveStep(2)}
            disabled={!(okUrl && okDocs && okAuth)}
          >
            Next
          </Button>
        </Box>
      );
    })()}
  </SectionCard>
)}


      {/* Step 2: Deploy Agent */}
      {activeStep === 2 && (
        <SectionCard sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Deploying Your API Test Container
          </Typography>

          {isDeploying ? (
            <>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Deploying...</Typography>
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ maxWidth: 800, mx: 'auto', textAlign: 'left', mb: 3 }}>
                <AlertTitle>Auto Deployment</AlertTitle>
                We’ll package your OpenAPI spec and configuration into a test container and deploy it automatically. This can take a couple of minutes.
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button variant="outlined" onClick={() => setActiveStep(1)}>
                  Back
                </Button>
                <Button variant="contained" onClick={handleSaveAndDeploy}>
                  Deploy
                </Button>
              </Box>
            </>
          )}
        </SectionCard>
      )}

      {/* Step 3: Done */}
      {activeStep === 3 && (
        <SectionCard sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%)' }}>
          <EmojiEventsIcon sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
            You’re all set! 🎉
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            Your API pentest project has been created. You can start tests and view results from your dashboard.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/pentests/')}>
              View Projects
            </Button>
          </Box>
        </SectionCard>
      )}
    </Box>
  );
};

export default CreateApiPentest;
